
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, ChevronDown, ChevronUp, Search, Info } from 'lucide-react';
import { YouTubeChannel, YouTubeChannelSelectionProps } from './types';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Loader2 } from 'lucide-react';

const YouTubeChannelSelection = ({
  channels,
  email,
  onSelectChannel,
  onCancel,
  remainingProfiles = 7,
  isLoading = false
}: YouTubeChannelSelectionProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [availableExpanded, setAvailableExpanded] = useState(true);
  const [connectedExpanded, setConnectedExpanded] = useState(true);
  const [helpExpanded, setHelpExpanded] = useState(false);

  const filteredChannels = channels.filter(channel => 
    channel.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Sélectionner les profils à connecter</h2>
            <Button variant="ghost" size="icon" onClick={onCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-gray-600">Chargement des chaînes YouTube...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-xl">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Sélectionner les profils à connecter</h2>
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center">
            <div className="bg-red-100 text-red-600 p-1 rounded mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9.5L9 4.5"/><path d="M14.5 4H9.5V9"/><path d="M21 16V20.4C21 20.78 20.78 21 20.4 21H3.6C3.22 21 3 20.78 3 20.4V16"/><path d="M6 12V16H18V12"/><rect width="18" height="4" x="3" y="12" rx=".4"/></svg>
            </div>
            <div>
              <p className="font-medium">Vous êtes connecté au compte : {email}</p>
              <p className="text-sm text-gray-500">Vous verrez les chaînes YouTube liées à ce compte</p>
            </div>
          </div>
          <Button variant="link" className="text-blue-600 p-0 h-auto text-sm ml-8 mt-1">
            Se connecter à un autre compte
          </Button>
        </div>
        
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-medium">{remainingProfiles} profils restants à connecter</p>
          <div className="relative w-64">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Rechercher un profil..." 
              className="pl-8" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <div className="max-h-[400px] overflow-y-auto">
          <Collapsible open={availableExpanded} onOpenChange={setAvailableExpanded} className="border-b border-gray-100">
            <div className="p-4">
              <CollapsibleTrigger className="flex items-center justify-between w-full">
                <div>
                  <h3 className="font-semibold text-left">Profils disponibles à la connexion</h3>
                  <p className="text-sm text-gray-500 text-left">Profils sociaux qui ne sont pas encore connectés à une organisation</p>
                </div>
                {availableExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-4 space-y-3">
                {filteredChannels.length > 0 ? (
                  filteredChannels.map(channel => (
                    <div key={channel.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                      <div className="flex items-center">
                        <div className="bg-red-100 p-1 rounded mr-3">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9.5L9 4.5"/><path d="M14.5 4H9.5V9"/><path d="M21 16V20.4C21 20.78 20.78 21 20.4 21H3.6C3.22 21 3 20.78 3 20.4V16"/><path d="M6 12V16H18V12"/><rect width="18" height="4" x="3" y="12" rx=".4"/></svg>
                        </div>
                        <div>
                          <p className="font-medium">{channel.title}</p>
                          <p className="text-xs text-gray-500">{`@${channel.title.toLowerCase().replace(/\s+/g, '')}`}</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-blue-600 border-blue-200"
                        onClick={() => onSelectChannel(channel)}
                      >
                        Connecter
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    {searchQuery ? "Aucun profil trouvé avec cette recherche" : "Aucune chaîne YouTube trouvée"}
                  </div>
                )}
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Collapsible open={connectedExpanded} onOpenChange={setConnectedExpanded} className="border-b border-gray-100">
            <div className="p-4">
              <CollapsibleTrigger className="flex items-center justify-between w-full">
                <div>
                  <h3 className="font-semibold text-left">Profils déjà connectés</h3>
                  <p className="text-sm text-gray-500 text-left">Profils sociaux déjà connectés à une organisation</p>
                </div>
                {connectedExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-4">
                <div className="mb-4">
                  <h4 className="text-sm font-medium mb-2">Dans cette organisation</h4>
                  <div className="text-gray-500 py-2">Aucun profil trouvé</div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium mb-2">Dans d'autres organisations</h4>
                  <div className="text-gray-500 py-2">Aucun profil trouvé</div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Collapsible open={helpExpanded} onOpenChange={setHelpExpanded}>
            <div className="p-4">
              <CollapsibleTrigger className="flex items-center justify-between w-full">
                <div className="flex items-center">
                  <Info className="h-5 w-5 mr-2 text-gray-500" />
                  <h3 className="font-medium text-left">Vous ne trouvez pas un profil ?</h3>
                </div>
                {helpExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Si vous ne trouvez pas la chaîne YouTube que vous recherchez :</p>
                <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                  <li>Vérifiez que vous êtes connecté avec le bon compte Google</li>
                  <li>Assurez-vous que la chaîne YouTube est associée à ce compte</li>
                  <li>Si vous venez de créer la chaîne, il peut y avoir un délai avant qu'elle n'apparaisse</li>
                </ul>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>
        
        <div className="p-4 flex justify-end">
          <Button onClick={onCancel}>
            J'ai fini de connecter des profils
          </Button>
        </div>
      </div>
    </div>
  );
};

export default YouTubeChannelSelection;
